import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Flex } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import AlloyFilterButton from '@adsk/alloy-react-filter-button';
import { PlusIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import SearchField from '@adsk/alloy-react-search-field';
import theme from '@adsk/alloy-react-theme';

import { pluginShape } from '../helpers/propTypes';

const renderPlusIcon = (p) => <PlusIcon {...p} />;

const StyledAlloyFilterButton = styled(AlloyFilterButton)({
  marginLeft: theme.spacing.S,
});

const EntityPickerModalTopBar = ({
  searchExpression,
  onSearchChange,
  showSearchBox,
  canCreate,
  onCreateClick,
  plugin,
  disabled,
  filtersAvailable,
  filtersOpen,
  filters,
  setFilters,
  toggleFiltersPanel,
  clearFilters,
  filtersCount,
  infoMessage,
  createFormProps,
  style = {},
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const {
    CreateNewButtonText,
    renderCreateNewButtonIcon,
    TopBarActions,
    CustomCreateNewButton,
    CustomPickerSearchBox,
    CustomFilterButton,
  } = plugin;

  const FilterButton = CustomFilterButton || StyledAlloyFilterButton;

  return (
    <Flex justifyContent="space-between" marginBottom="16px" style={style}>
      <Flex alignItems="center">
        {canCreate &&
          (CustomCreateNewButton ? (
            <CustomCreateNewButton
              onCreateClick={onCreateClick}
              disabled={disabled}
              createFormProps={createFormProps}
            />
          ) : (
            <Button
              onClick={onCreateClick}
              variant={Button.VARIANTS.SECONDARY}
              renderIcon={renderCreateNewButtonIcon || renderPlusIcon}
              disabled={disabled}
              data-testid="EntityPickerModalTopBar__create-button"
            >
              {CreateNewButtonText ? (
                <CreateNewButtonText />
              ) : (
                T.translate('CREATE')
              )}
            </Button>
          ))}
        {infoMessage}
      </Flex>
      <Flex alignItems="center">
        {showSearchBox &&
          (CustomPickerSearchBox ? (
            <CustomPickerSearchBox
              disabled={disabled}
              searchExpression={searchExpression}
              onSearchChange={onSearchChange}
            />
          ) : (
            <SearchField
              style={{
                width: 300,
                'input:disabled': {
                  background: theme.colors.white,
                },
              }}
              placeholder={T.translate('SEARCH')}
              value={searchExpression || ''}
              onChange={onSearchChange}
              disabled={disabled}
            />
          ))}
        {filtersAvailable && (
          <FilterButton
            filtersOpen={filtersOpen}
            filters={filters}
            onClick={toggleFiltersPanel}
            clearFilters={clearFilters}
            filtersCount={filtersCount}
            disabled={disabled}
            data-testid="EntityPickerModalTopBar__filter-button"
            {...(CustomFilterButton && {
              setFilters,
            })}
          />
        )}
        {TopBarActions && <TopBarActions />}
      </Flex>
    </Flex>
  );
};

EntityPickerModalTopBar.propTypes = {
  /**
   * Whether or not to show search box.
   */
  showSearchBox: PropTypes.bool.isRequired,
  /**
   * Current search expression
   */
  searchExpression: PropTypes.string,
  /**
   * Callback when search expression is changed
   */
  onSearchChange: PropTypes.func.isRequired,
  /**
   * Whether the user can create entity of selected type.
   */
  canCreate: PropTypes.bool.isRequired,
  /**
   * Callback when clicking create button
   */
  onCreateClick: PropTypes.func.isRequired,
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape,
  /**
   * Whether we're currently in disabled state (saving process, etc.).
   */
  disabled: PropTypes.bool,
  /**
   * Whether we have filters panel for current plugin.
   */
  filtersAvailable: PropTypes.bool,
  /**
   * Whether or not filter panel is open.
   */
  filtersOpen: PropTypes.bool.isRequired,
  /**
   * Current filters.
   */
  filters: PropTypes.shape({
    searchExpression: PropTypes.string,
  }).isRequired,
  /**
   * Callback to set filters.
   */
  setFilters: PropTypes.func,
  /**
   * Callback to toggle filter panel open/close.
   */
  toggleFiltersPanel: PropTypes.func.isRequired,
  /**
   * Callback to clear filters.
   */
  clearFilters: PropTypes.func.isRequired,
  /**
   * Number of currently applied filters.
   */
  filtersCount: PropTypes.number.isRequired,
  /**
   * Top bar info message markup
   */
  infoMessage: PropTypes.node,
  /**
   * Object containing locationId data
   */
  createFormProps: PropTypes.object,
  /**
   * Whether or not the plugin is disabled in entitlements.
   */
  isPluginDisabledInEntitlements: PropTypes.bool,
  /**
   * Styles applied to the root element
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default EntityPickerModalTopBar;
